import * as THREE from "three/webgpu";
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchPointObject} from './SketchPointObject';
import type {SketchContour} from '../meshProxy/SketchContour';
import type {SketchCollectionObject} from './SketchCollectionObject';
import {AbstractSketchEdgeObject, type AbstractSketchEdgeObjectOptions,} from './AbstractSketchEdgeObject';
import type {SketchSlotObject} from './SketchSlotObject';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import {isSketchLineObject} from '../guards/isSketchLineObject';
import {
    resolveEdgeTravelTangentAtEndpoint,
    isTangentArcSourceEdge,
    getSourceEdgeStartPoint,
    getSourceEdgeEndPoint,
    type TangentArcSourceEdge,
} from '../../utils/sketch/resolveEdgeTravelTangentAtEndpoint';
import {SketchPointKind} from './ISketchObject';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {SketchCloneContext} from './SketchCloneContext';
import {ensureRaycastAccelerationForObjectTree, refitRaycastAccelerationForGeometry,} from '../../raycast-acceleration';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';

/**
 * 弧线定义参数。
 * 说明：
 * 1. 所有角度均为弧度制；
 * 2. 角度基于参考面局部坐标系；
 * 3. center 为世界坐标，用于后续重建几何。
 */
export interface SketchArcDefinition {
    /** 圆心（世界坐标）。 */
    centerWorld: THREE.Vector3;
    /** 半径。 */
    radius: number;
    /** 起始角。 */
    startAngle: number;
    /** 终止角。 */
    endAngle: number;
    /** 是否顺时针。 */
    clockwise: boolean;
    /** 采样段数。 */
    segments?: number;
}

export interface SketchArcObjectOptions extends AbstractSketchEdgeObjectOptions {
    /** 创建时所属抽象参考面。 */
    referencePlane: ReferencePlane;
    /** 弧线定义*/
    definition: SketchArcDefinition;

    /** 是否为绘制过程中的临时预览圆弧。 */
    isDrawingPreviewArc?: boolean;

    /** 圆心控制点。 */
    centerPoint?: SketchPointObject;
    /** 圆心是否由其它对象树节点持有；反序列化共享圆心时必须显式声明。 */
    centerPointOwnership?: 'arc' | 'external';
    startPoint: SketchPointObject
    endPoint: SketchPointObject
}

/**
 * 草图弧线网格对象
 * 职责
 * 1. 使用 THREE.Line 表达一条草图弧线；
 * 2. 统一承接线状实体基础行为
 * 3. 提供弧线参数更新与几何重建能力
 */
export class SketchArcObject extends AbstractSketchEdgeObject {

    /** 半圆扫掠角常量。 */
    private static readonly HALF_CIRCLE_SWEEP = Math.PI;


    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Arc;
    /** 创建时所属抽象参考面。 */
    public referencePlane: ReferencePlane;
    isSketchArcObject = true;

    /**
     * 是否为绘制过程中的临时预览圆弧。
     * 说明：
     * 1. 该标记用于区分正式草图圆弧与绘制态预览圆弧；
     * 2. 为 true 时，点捕捉链路必须忽略该对象；
     * 3. 默认值为 false，仅绘制处理器创建的预览圆弧会显式置为 true。
     */
    public isDrawingPreviewArc: boolean = false;

    /** 弧线起点控制点*/
    private arcStartPoint: SketchPointObject;

    /** 弧线终点控制点*/
    private arcEndPoint: SketchPointObject;

    /** 圆心控制点。 */
    private arcCenterPoint: SketchPointObject;


    /** 默认中点对象。 */
    private readonly midPoint: SketchPointObject;


    /** 弧线几何定义*/
    private arcDefinition: Required<SketchArcDefinition>;

    /** 上一次完成几何同步后的圆心世界坐标快照。 */
    private lastSyncedCenterWorld: THREE.Vector3 | null = null;

    /** 圆心平移联动期间的重入保护标记。 */
    private isCenterTranslateSyncing = false;

    /** 默认中点平移联动期间的重入保护标记。 */
    private isMidPointTranslateSyncing = false;

    /** 关联的封闭线段几何对象。 */
    public closedCollection: SketchCollectionObject | SketchSlotObject | null = null;


    /**
     * 四个标准象限角（弧度）：0°、90°、180°、270°。
     * 用于在构造时创建对应的象限点对象。
     */
    private static readonly QUADRANT_ANGLES: readonly number[] = [
        0,
        Math.PI / 2,
        Math.PI,
        Math.PI * 3 / 2,
    ];

    /** 象限点集合，每个元素对应一个标准象限角，按 QUADRANT_ANGLES 顺序存储。 */
    private readonly arcQuadrantPoints: Set<SketchPointObject> = new Set();

    /** 象限角到象限点对象的映射，用于同步时定位具体点。 */
    private readonly angleToQuadrantPoint: Map<number, SketchPointObject> = new Map();


    /** 实例计数，用于生成显示名称*/
    static count = 0;

    public get baseDisplayName(): string {
        return '圆弧';
    }

    /**
     * 构造草图弧线对象
     * @param options 构造参数
     */
    public constructor(options: SketchArcObjectOptions) {
        super(options);
        this.referencePlane = options.referencePlane;

        // 在构造阶段写入预览圆弧标记，确保捕捉链路可直接读取。
        this.isDrawingPreviewArc = options.isDrawingPreviewArc ?? false;

        this.arcStartPoint = options.startPoint;
        this.arcEndPoint = options.endPoint;
        this.arcCenterPoint = options.centerPoint ?? new SketchPointObject({
            position: options.definition.centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: options.referencePlane,
        });
        if (!options.centerPoint) {
            this.arcCenterPoint.visible = false;
        }
        this.arcDefinition = {
            ...options.definition,
            segments: options.definition.segments ?? 64,
        };

        // 圆弧和线段保持一致：统一维护默认中点对象供中点捕捉链路复用。
        this.midPoint = new SketchPointObject({
            position: this.getMidpointPosition(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: options.referencePlane,
        });
        this.midPoint.attachMidPointRole();


        if (this.isDrawingPreviewArc) {
            // 预览圆弧自带的起点、终点、圆心点都不应参与点捕捉。
            this.arcStartPoint.isDrawingPreviewPoint = true;
            this.arcEndPoint.isDrawingPreviewPoint = true;
            this.arcCenterPoint.isDrawingPreviewPoint = true;
            // 预览圆弧默认中点同样不可参与点捕捉。
            this.midPoint.isDrawingPreviewPoint = true;
        }

        this.arcStartPoint.registerConnectedEdge(this);
        this.arcEndPoint.registerConnectedEdge(this);
        this.arcCenterPoint.registerConnectedEdge(this);
        this.midPoint.registerConnectedEdge(this);
        this.arcStartPoint.attachEndpointRole();
        this.arcEndPoint.attachEndpointRole();
        this.registerRelatedSketchPoints([
            this.arcStartPoint,
            this.arcEndPoint,
            this.arcCenterPoint,
            this.midPoint,
        ]);

        this.attachEndpointControlPoint(this.arcStartPoint);
        this.attachEndpointControlPoint(this.arcEndPoint);
        this.attachCenterControlPoint(this.arcCenterPoint, options.centerPointOwnership);

        this.attachMidPoint(this.midPoint);


        // 构造时创建四个象限点对象，初始可见性由 syncArcQuadrantPoints 根据弧范围决定。
        this.createArcQuadrantPoints();

        this.lastSyncedCenterWorld = this.arcCenterPoint.getPosition();

        this.name = `${this.baseDisplayName}${SketchArcObject.count}`;
        SketchArcObject.count += 1;

        /**
         * 构造完成后立即初始化圆弧几何。
         * 说明：
         * 1. 圆回退为圆弧、圆角落地等链路会在创建后立刻参与显示与后续交互；
         * 2. 若此时 geometry 尚未生成，外部读取几何顶点、包围盒或调试信息时会得到空对象；
         * 3. 在构造阶段完成首次几何同步，可保证新建圆弧从创建瞬间起就是完整可渲染状态。
         */
        this.updateGeometry();
    }

    /**
     * 更新弧线定义并刷新几何
     * @param definition 新弧线定义
     */
    public updateArcDefinition(definition: SketchArcDefinition): void {
        this.arcDefinition = {
            ...definition,
            // 调用方可能复用 scratch 向量，圆弧定义必须持有独立中心。
            centerWorld: definition.centerWorld.clone(),
            segments: definition.segments ?? this.arcDefinition.segments,
        };
        this.updateArc();
        /**
         * 定义 setter 必须同步维护控制点结构不变量；几何关系求解已迁到参数系统，
         * 这里仅按新定义静默派生圆心与端点，禁止重新触发对象式约束链。
         */
        this.syncControlPointsFromDefinition();
    }

    /**
     * 写入统一参数求解器已经确认的圆弧定义并刷新几何。
     *
     * 与面向属性编辑的 {@link updateArcDefinition} 不同，本入口不会依据角度和半径
     * 重新派生控制点。参数求解运行时会先原子写回圆心与端点，再写回半径自由度；
     * 若这里再次同步控制点，就会用旧定义反向覆盖已经满足全部公式的点解。
     *
     * @param definition 已与当前控制点一致的圆弧定义。
     */
    public applySolvedArcDefinition(definition: SketchArcDefinition): void {
        this.arcDefinition = {
            ...definition,
            // 参数运行时会复用写回向量，已求解定义同样不能保留外部可变引用。
            centerWorld: definition.centerWorld.clone(),
            segments: definition.segments ?? this.arcDefinition.segments,
        };

        // 求解终态中的控制点是权威值，此处只刷新依赖定义的圆弧几何与辅助点。
        this.updateArc();
    }

    /**
     * 按当前定义刷新弧线几何
     */
    public updateArc(): void {
        this.updateGeometry();
    }

    /**
     * 获取圆心控制点。
     * @returns 圆心控制点对象。
     */
    public getCenterPoint(): SketchPointObject {
        return this.arcCenterPoint;
    }


    /**
     * 获取圆弧默认中点对象。
     * @returns 圆弧默认中点对象。
     */
    public getMidPoint(): SketchPointObject {
        return this.midPoint;
    }


    /**
     * 获取圆弧起点控制点。
     * @returns 圆弧起点控制点。
     */
    public getStartPoint(): SketchPointObject {
        return this.arcStartPoint;
    }

    /**
     * 获取圆弧终点控制点。
     * @returns 圆弧终点控制点。
     */
    public getEndPoint(): SketchPointObject {
        return this.arcEndPoint;
    }


    /**
     * 替换圆弧起点并同步圆弧定义。
     *
     * 设计说明：
     * 1. 端点替换必须同步维护连接边、关联草图点和端点角色引用计数；
     * 2. 圆角修剪会显式传入保持原圆心与半径的新定义；
     * 3. 其它调用未传定义时，按新控制点位置重建当前圆弧，保证公共 API 不留下几何不同步状态。
     *
     * @param point 新起点控制点。
     * @param definition 可选的新圆弧定义。
     */
    public setStartPoint(point: SketchPointObject, definition?: SketchArcDefinition): void {
        this.setEdgeStartPoint(point);
        this.finishEndpointReplacement(definition);
    }

    /**
     * 替换圆弧终点并同步圆弧定义。
     *
     * @param point 新终点控制点。
     * @param definition 可选的新圆弧定义。
     */
    public setEndPoint(point: SketchPointObject, definition?: SketchArcDefinition): void {
        this.setEdgeEndPoint(point);
        this.finishEndpointReplacement(definition);
    }

    /**
     * 完成端点替换后的圆弧定义与控制点同步。
     * @param definition 可选的新圆弧定义。
     */
    private finishEndpointReplacement(definition?: SketchArcDefinition): void {
        if (definition) {
            // 圆角修剪必须保留源圆弧圆心和半径，因此以求解结果为唯一几何来源。
            this.updateArcDefinition(definition);
            this.syncControlPointsFromDefinition();
            return;
        }

        // 普通端点替换按当前控制点重建定义，避免旧定义覆盖新端点位置。
        this.rebuildDefinitionFromControlPoints();
        this.updateArc();
        this.syncEndpointPositions();
    }


    /**
     * 按当前圆弧定义同步控制点位置。
     * 说明：
     * 1. 裁剪与撤销恢复链路会先更新圆弧定义，再要求对象把控制点回写到定义对应位置；
     * 2. 这里统一复用圆心与端点的静默同步逻辑，避免再次触发约束求解；
     * 3. 中点与象限点由几何刷新链路维护，这里只负责外部直接依赖的控制点对象。
     */
    public syncControlPointsFromDefinition(): void {
        this.syncCenterPoint();
        this.syncEndpointPositions();
    }


    /**
     * 获取当前弧线定义快照
     * @returns 弧线定义副本
     */
    public getArcDefinition(): Required<SketchArcDefinition> {
        return {
            ...this.arcDefinition,
            centerWorld: this.arcDefinition.centerWorld.clone(),
        };
    }


    /**
     * 复制当前圆弧对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前圆弧对象的独立副本。
     */
    public cloneSelf(context?: SketchCloneContext): SketchArcObject {
        const clonedArc = new SketchArcObject({
            ...this.createCloneBaseOptions(),
            referencePlane: this.referencePlane,
            definition: this.getArcDefinition(),

            isDrawingPreviewArc: this.isDrawingPreviewArc,

            startPoint: this.arcStartPoint.cloneSelf(context),
            endPoint: this.arcEndPoint.cloneSelf(context),
            centerPoint: this.arcCenterPoint.cloneSelf(context),
        });
        this.registerObjectClone(context, clonedArc);

        clonedArc.getStartPoint().visible = this.arcStartPoint.visible;
        clonedArc.getEndPoint().visible = this.arcEndPoint.visible;
        clonedArc.getCenterPoint().visible = this.arcCenterPoint.visible;

        clonedArc.getMidPoint().visible = this.midPoint.visible;
        clonedArc.getMidPoint().isActiveForSnap = this.midPoint.isActiveForSnap;
        if (context) {
            context.pointMap.set(this.midPoint.uuid, clonedArc.getMidPoint());
            context.objectMap.set(this.midPoint.uuid, clonedArc.getMidPoint());
        }

        // 同步象限点映射，保证草图组复制可重建多目标关系。
        this.angleToQuadrantPoint.forEach((sourcePoint, angle) => {
            const clonedPoint = clonedArc.angleToQuadrantPoint.get(angle);
            if (!clonedPoint) {
                throw new Error('圆弧对象复制失败：象限点映射缺失。');
            }
            clonedPoint.visible = sourcePoint.visible;
            clonedPoint.isActiveForSnap = sourcePoint.isActiveForSnap;
            if (context) {
                context.pointMap.set(sourcePoint.uuid, clonedPoint);
                context.objectMap.set(sourcePoint.uuid, clonedPoint);
            }
        });

        // 复制圆弧对象的单目标几何关系（如半径约束）。
        this.cloneSingleTargetConstraintsTo(clonedArc, context);
        this.syncCommonDisplayStateToClone(clonedArc);
        return clonedArc;
    }


    /**
     * 获取圆弧中点位置（世界坐标系）。
     * 说明：
     * 1. 根据起始角、终止角和旋转方向计算扫掠角的中分角度；
     * 2. 在参考面局部坐标系中计算中分角度对应的弧上位置；
     * 3. 转换为世界坐标返回。
     * @returns 弧上中点的世界坐标。
     */
    public getMidpointPosition(): THREE.Vector3 {
        const { startAngle, endAngle, clockwise, radius, centerWorld } = this.arcDefinition;

        // 根据旋转方向计算扫掠角中分点。
        let midAngle: number;
        if (clockwise) {
            // 顺时针：角度从 startAngle 递减到 endAngle。
            let sweep = startAngle - endAngle;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }
            midAngle = startAngle - sweep / 2;
        } else {
            // 逆时针：角度从 startAngle 递增到 endAngle。
            let sweep = endAngle - startAngle;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }
            midAngle = startAngle + sweep / 2;
        }

        // 在参考面局部坐标系中计算弧上中点。
        const localCenter = this.referencePlane.worldToLocal(centerWorld);
        const localX = localCenter.x + radius * Math.cos(midAngle);
        const localY = localCenter.y + radius * Math.sin(midAngle);

        // 转换为世界坐标。
        return this.referencePlane.localToWorld(localX, localY);
    }

    /**
     * 平移弧线（通过平移圆心、起点与终点实现）
     * @param delta 位移向量
     * @throws 当圆弧起点或终点被固定而无法整体平移时抛出错误。
     */
    public translateArc(delta: THREE.Vector3): void {
        if (this.arcStartPoint.isFixed() || this.arcEndPoint.isFixed()) {
            throw new Error('Arc endpoint is fixed and Arc translation is blocked.');
        }

        const nextCenter = this.arcDefinition.centerWorld.clone().add(delta);
        const nextStart = this.arcStartPoint.getPosition().add(delta);
        const nextEnd = this.arcEndPoint.getPosition().add(delta);

        this.arcDefinition.centerWorld = nextCenter;
        this.arcStartPoint.setPosition(nextStart);
        this.arcEndPoint.setPosition(nextEnd);
        this.updateArc();
    }


    /**
     * 通过圆心点拖动语义平移圆弧。
     *
     * 设计说明：
     * 1. 圆弧中心点拖动时，`syncEndpointsToGeometry` 会按圆心位移同步起点与终点；
     * 2. 复制实体、移动实体中的圆弧平移应与手动拖动圆心保持同一条几何收口链路；
     * 3. 因此这里只移动圆心控制点，并把触发点显式设置为圆心点，让现有圆心拖动逻辑完成定义重建、中点与象限点同步。
     *
     * @param delta 位移向量。
     * @throws 当圆心、起点或终点中任一控制点被固定时抛出错误。
     */
    public translateArcByCenterPoint(delta: THREE.Vector3): void {
        if (this.arcCenterPoint.isFixed() || this.arcStartPoint.isFixed() || this.arcEndPoint.isFixed()) {
            throw new Error('Arc control point is fixed and center point translation is blocked.');
        }

        const nextCenterWorld = this.arcCenterPoint.getPosition().add(delta);
        this.arcCenterPoint.setPosition(nextCenterWorld);
    }


    protected getEdgeStartPoint(): SketchPointObject {
        return this.arcStartPoint;
    }

    protected getEdgeEndPoint(): SketchPointObject {
        return this.arcEndPoint;
    }

    /**
     * 按圆心位移差量平移圆弧起终点。
     * 说明：
     * 1. 圆角圆弧的圆心拖拽本质是整条圆弧平移，而不是“重算一个新圆弧”；
     * 2. 因此必须把与圆弧共用的起终点按同一差量平移，保持圆弧与相邻线段的拓扑连续性；
     * 3. 点对象只负责通知派生几何刷新，不会启动第二套约束传播。
     * @param delta 圆心相对上一次同步结果的世界坐标差量。
     */
    private syncStartEndByCenterDelta(delta: THREE.Vector3): void {
        const nextStartWorld = this.arcStartPoint.getPosition().add(delta);
        const nextEndWorld = this.arcEndPoint.getPosition().add(delta);
        this.arcStartPoint.setPosition(nextStartWorld);
        this.arcEndPoint.setPosition(nextEndWorld);
    }

    /**
     * 按默认中点位移差量平移整条圆弧。
     * 说明：
     * 1. 默认中点是圆弧拖拽时的“刚体平移句柄”，不是用于重算半径或角度的定义点；
     * 2. 因此中点被拖动时必须同步移动圆心、起点与终点，保持半径和起止角定义不变；
     * 3. 使用点对象公开落位 API，可继续通知共享端点上的相邻几何刷新。
     * @param delta 中点相对当前圆弧定义中点的世界坐标差量。
     */
    private syncControlPointsByMidPointDelta(delta: THREE.Vector3): void {
        if (this.arcCenterPoint.isFixed() || this.arcStartPoint.isFixed() || this.arcEndPoint.isFixed()) {
            throw new Error('Arc control point is fixed and midpoint translation is blocked.');
        }

        const nextCenterWorld = this.arcCenterPoint.getPosition().add(delta);
        const nextStartWorld = this.arcStartPoint.getPosition().add(delta);
        const nextEndWorld = this.arcEndPoint.getPosition().add(delta);

        this.arcCenterPoint.setPosition(nextCenterWorld);
        this.arcStartPoint.setPosition(nextStartWorld);
        this.arcEndPoint.setPosition(nextEndWorld);
    }

    /**
     * 同步端点与圆弧定义到几何，并在拖拽圆心时保持弧形不变。
     * @param movedPoint 触发刷新的控制点。
     * @throws 当圆心位移需要同步端点，但起点或终点已被固定时抛出错误。
     */
    public override syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        const draggedEndpointRole = this.resolveDraggedEndpointRole(movedPoint);

        if (this.isMidPointTranslateSyncing && movedPoint !== this.midPoint) {
            return;
        }

        if (this.isCenterTranslateSyncing && movedPoint !== this.arcCenterPoint) {
            return;
        }

        if (!this.isCenterTranslateSyncing && movedPoint === this.arcCenterPoint) {
            const previousCenterWorld = this.lastSyncedCenterWorld;
            const currentCenterWorld = this.arcCenterPoint.getPosition();
            if (previousCenterWorld) {
                const centerDelta = currentCenterWorld.clone().sub(previousCenterWorld);
                if (centerDelta.lengthSq() > SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
                    if (this.arcStartPoint.isFixed() || this.arcEndPoint.isFixed()) {
                        throw new Error('Arc endpoint is fixed and center translation is blocked.');
                    }
                    this.isCenterTranslateSyncing = true;
                    try {
                        this.syncStartEndByCenterDelta(centerDelta);
                    } finally {
                        this.isCenterTranslateSyncing = false;
                    }
                }
            }
        }

        if (!this.isMidPointTranslateSyncing && movedPoint === this.midPoint) {
            const previousMidWorld = this.getMidpointPosition();
            const currentMidWorld = this.midPoint.getPosition();
            const midPointDelta = currentMidWorld.clone().sub(previousMidWorld);
            if (midPointDelta.lengthSq() > SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
                this.isMidPointTranslateSyncing = true;
                try {
                    this.syncControlPointsByMidPointDelta(midPointDelta);
                } finally {
                    this.isMidPointTranslateSyncing = false;
                }
            }
        }

        if (draggedEndpointRole) {
            this.rebuildDefinitionFromDraggedEndpoint(draggedEndpointRole);
        } else {

            /**
             * 圆弧几何刷新依赖 `arcDefinition`，而 `updateGeometry()` 内部会把定义中的圆心回写到控制点。
             * 若这里直接进入基类刷新，旧定义会把刚拖动的新圆心静默覆盖回旧位置，
             * 进而在后续重建时形成“旧圆心 + 新起终点”，表现为圆弧被错误放大。
             * 因此必须先基于当前控制点收敛一次定义，再进入基类几何刷新链路。
             */
            this.rebuildDefinitionFromControlPoints();

        }

        super.syncEndpointsToGeometry(movedPoint);

        if (!draggedEndpointRole) {
            // super 结束后再收敛定义，保证求解后的控制点位置会写回圆弧定义。
            this.rebuildDefinitionFromControlPoints();
        }
        this.updateGeometry();
        // 最终将起终点控制点吸附到圆弧圆周精确位置，消除因端点不在圆周上导致的飘逸。
        // 注意：必须在 rebuildDefinitionFromControlPoints 读取拖动位置之后调用，避免干扰定义重建。
        this.syncEndpointPositions();
        this.lastSyncedCenterWorld = this.arcCenterPoint.getPosition();
    }


    protected setEdgeStartPoint(point: SketchPointObject): void {
        this.replaceEndpointControlPoint('start', point);
    }


    /**
     * 挂载端点控制点。
     * @param point 控制点对象。
     */
    private attachEndpointControlPoint(point: SketchPointObject): void {
        /**
         * 端点角色由构造函数或替换入口统一登记。
         * 这里仅负责 Three.js 父子关系，避免共享端点的角色引用计数重复累加。
         */
        if (!point.parent) {
            this.add(point);
        }
    }


    /**
     * 挂载圆心控制点并写入圆心角色元数据。
     * @param point 圆心控制点。
     * @param ownership 圆心对象树所有权。
     */
    private attachCenterControlPoint(
        point: SketchPointObject,
        ownership: 'arc' | 'external' | undefined,
    ): void {
        // 圆弧中心与圆、椭圆共用中心十字视觉语义，交由点对象统一收口材质。
        point.attachCurveCenterControlPointRole();
        // 未声明时保持既有绘制行为；只有点池共享圆心明确由外部持有。
        if (ownership !== 'external' && !point.parent) {
            this.add(point);
        }
    }


    /**
     * 挂载默认中点对象并写入中点角色元数据。
     * @param point 默认中点对象。
     */
    private attachMidPoint(point: SketchPointObject): void {
        point.attachMidPointRole();
        this.add(point);
    }


    /**
     * 解除控制点挂载并清理元数据。
     * @param point 控制点对象。
     */
    private detachControlPoint(point: SketchPointObject): void {
        if (point.parent === this) {
            this.remove(point);
        }
    }

    /**
     * 根据控制点位置重建圆弧定义，保持原有顺逆时针方向。
     */
    private rebuildDefinitionFromControlPoints(): void {
        const centerWorld = this.arcCenterPoint.getPosition();
        const startWorld = this.arcStartPoint.getPosition();
        const endWorld = this.arcEndPoint.getPosition();

        const centerLocal = this.referencePlane.worldToLocal(centerWorld);
        const startLocal = this.referencePlane.worldToLocal(startWorld);
        const endLocal = this.referencePlane.worldToLocal(endWorld);

        const measuredRadius = Math.hypot(startLocal.x - centerLocal.x, startLocal.y - centerLocal.y);
        if (!Number.isFinite(measuredRadius) || measuredRadius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        const radius = measuredRadius;

        const startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
        const rawEndAngle = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);
        let endAngle = rawEndAngle;
        if (this.arcDefinition.clockwise) {
            if (endAngle > startAngle) {
                endAngle -= Math.PI * 2;
            }
        } else if (endAngle < startAngle) {
            endAngle += Math.PI * 2;
        }

        this.arcDefinition = {
            ...this.arcDefinition,
            centerWorld,
            radius,
            startAngle,
            endAngle,
        };
    }


    /**
     * 按半径锁定语义重建圆弧定义。
     *
     * 说明：
     * 1. 圆角圆弧存在半径约束时，半径是约束主尺度，不能在端点刷新时被弦长反推覆盖；
     * 2. 参数运行时已经把圆心和端点写到最终解位置，这里只读取角度并保留既有半径；
     * 3. 最终端点会由 `syncEndpointPositions` 吸附回锁定半径圆周，避免浮点误差累积。
     */
    private rebuildRadiusLockedDefinitionFromControlPoints(): void {
        const lockedRadius = this.arcDefinition.radius;
        if (!Number.isFinite(lockedRadius) || lockedRadius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        const centerWorld = this.arcCenterPoint.getPosition();
        const startWorld = this.arcStartPoint.getPosition();
        const endWorld = this.arcEndPoint.getPosition();
        const centerLocal = this.referencePlane.worldToLocal(centerWorld);
        const startLocal = this.referencePlane.worldToLocal(startWorld);
        const endLocal = this.referencePlane.worldToLocal(endWorld);
        const startVector = startLocal.clone().sub(centerLocal);
        const endVector = endLocal.clone().sub(centerLocal);
        if (
            startVector.length() <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength
            || endVector.length() <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength
        ) {
            return;
        }

        const startAngle = Math.atan2(startVector.y, startVector.x);
        const rawEndAngle = Math.atan2(endVector.y, endVector.x);
        let endAngle = rawEndAngle;
        if (this.arcDefinition.clockwise) {
            if (endAngle > startAngle) {
                endAngle -= Math.PI * 2;
            }
        } else if (endAngle < startAngle) {
            endAngle += Math.PI * 2;
        }

        this.arcDefinition = {
            ...this.arcDefinition,
            centerWorld,
            radius: lockedRadius,
            startAngle,
            endAngle,
        };
    }

    /**
     * 判断当前圆弧是否存在半径约束。
     * @returns 存在半径约束返回 true。
     */
    private hasRadiusConstraint(): boolean {
        return this.constraints.hasType(SketchRelationKind.Radius);
    }


    /**
     * 解析当前拖拽点是否为圆弧端点。
     * @param point 当前触发点。
     * @returns 若为起点或终点，返回对应角色；否则返回 null。
     */
    private resolveDraggedEndpointRole(point?: SketchPointObject): 'start' | 'end' | null {
        if (point === this.arcStartPoint) {
            return 'start';
        }
        if (point === this.arcEndPoint) {
            return 'end';
        }
        return null;
    }

    /**
     * 根据端点拖拽语义重建圆弧定义。
     *
     * 说明：
     * 1. 被拖拽端点自由移动，另一端点保持不变；
     * 2. 当弦长变大时，圆弧扫掠角朝 180 度收敛；
     * 3. 当弦长变小时，小弧继续变小，大弧继续变大并逐渐趋向整圆。
     *
     * @param draggedRole 被拖拽端点角色。
     */
    private rebuildDefinitionFromDraggedEndpoint(draggedRole: 'start' | 'end'): void {
        const fixedPoint = draggedRole === 'start'
            ? this.arcEndPoint
            : this.arcStartPoint;
        const draggedPoint = draggedRole === 'start'
            ? this.arcStartPoint
            : this.arcEndPoint;

        const fixedWorld = fixedPoint.getPosition();
        const draggedWorld = draggedPoint.getPosition();
        const nextChordLength = fixedWorld.distanceTo(draggedWorld);
        if (!Number.isFinite(nextChordLength) || nextChordLength <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        const previousStartWorld = this.resolveArcWorldPointByAngle(this.arcDefinition.startAngle);
        const previousEndWorld = this.resolveArcWorldPointByAngle(this.arcDefinition.endAngle);
        const previousChordLength = previousStartWorld.distanceTo(previousEndWorld);
        const previousSweep = this.resolveSweepFromDefinition(this.arcDefinition);
        const nextSweep = this.resolveSweepForEndpointDrag(previousSweep, previousChordLength, nextChordLength);
        const nextDefinition = this.buildArcDefinitionFromEndpointsAndSweep(
            draggedRole === 'start' ? draggedWorld : fixedWorld,
            draggedRole === 'start' ? fixedWorld : draggedWorld,
            nextSweep,
        );

        if (!nextDefinition) {
            return;
        }

        this.arcDefinition = {
            ...this.arcDefinition,
            ...nextDefinition,
        };
    }

    /**
     * 相切锚定端点拖拽重建：以"线-弧共享切点 + 直线方向"构造相切圆弧。
     *
     * 说明：
     * 1. 切线弧（首段从直线端点起步并登记相切关系）拖动其任一端点时，
     *    相切必须由圆弧自身的几何参数（圆心/半径）满足，而不是由相切约束
     *    平移直线或圆弧——否则共享切点会被从鼠标位置拽走、直线远端点被大幅平移；
     * 2. 数学构造：设 A 为共享切点（锚点）、E 为弧的另一端点、d 为直线单位方向，
     *    A 处半径方向取 r̂ = ±rot90(d)，圆心 O = A + ρ·r̂，
     *    由 |O−E| = |O−A| = |ρ| 解得 ρ = |E−A|² / (2·r̂·(E−A))；
     *    两个候选（r̂ 取 ±）中取圆心离旧圆心近的一侧，保持圆弧不翻转；
     * 3. 退化（弦塌缩、E 位于切向延长线使 ρ 非有限、半径塌缩）时返回 false，
     *    由调用方回退到普通端点拖拽语义。
     *
     * @param _draggedRole 被拖拽端点角色（仅用于回退语义，构造不依赖角色）。
     * @returns 构造成功返回 true；否则返回 false。
     */
    private rebuildTangentAnchoredArcDefinition(_draggedRole: 'start' | 'end'): boolean {
        // 仅在弧上存在与来源草图边（直线/圆弧/椭圆弧/抛物线/样条）共享端点的相切关系时启用切线构造。
        const tangentSource = this.resolveSharedTangentSourceEdge();
        if (!tangentSource) {
            return false;
        }

        // 解析共享切点（锚点）与弧的另一端点：构造不依赖被拖端点角色，
        // 无论拖共享切点还是自由端，都由"锚点 + 切线方向 + 另一端点"唯一确定圆。
        const sharedPoint = this.arcStartPoint === tangentSource.sharedPoint
            ? this.arcStartPoint
            : (this.arcEndPoint === tangentSource.sharedPoint ? this.arcEndPoint : null);
        if (!sharedPoint) {
            return false;
        }
        const freePoint = sharedPoint === this.arcStartPoint ? this.arcEndPoint : this.arcStartPoint;

        const plane = this.referencePlane;
        const anchorLocal = plane.worldToLocal(sharedPoint.getPosition());
        const freeLocal = plane.worldToLocal(freePoint.getPosition());
        const chord = freeLocal.clone().sub(anchorLocal);
        const chordLengthSq = chord.lengthSq();
        const linearTolerance = SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength;
        // 弦塌缩（两端点重合）时不存在过两点的圆，直接退化。
        if (chordLengthSq <= linearTolerance * linearTolerance) {
            return false;
        }

        // 切点处切线方向：直线取拖拽后线端点的解析位置差（共享切点被拖时方向随交点自然变化），
        // 曲线取来源边在共享点处的行进切线（无向，构造时 ± 两个候选已覆盖方向）。
        let tangentDirectionLocal: THREE.Vector2 | null = null;
        if (isSketchLineObject(tangentSource.edge)) {
            tangentDirectionLocal = plane.worldToLocal(tangentSource.edge.endPoint.getPosition())
                .sub(plane.worldToLocal(tangentSource.edge.startPoint.getPosition()));
        } else {
            const travelTangent = resolveEdgeTravelTangentAtEndpoint(tangentSource.edge, sharedPoint);
            if (travelTangent) {
                tangentDirectionLocal = plane.worldToLocal(
                    sharedPoint.getPosition().clone().add(travelTangent),
                ).sub(anchorLocal);
            }
        }
        if (!tangentDirectionLocal || tangentDirectionLocal.length() <= linearTolerance) {
            return false;
        }
        const lineDirectionNorm = tangentDirectionLocal.clone().normalize();
        // 切点处半径方向候选：直线方向逆时针转 90° 的两个相反侧。
        const perpendicular = new THREE.Vector2(-lineDirectionNorm.y, lineDirectionNorm.x);

        const previousCenterLocal = plane.worldToLocal(this.arcDefinition.centerWorld);
        let bestCenterLocal: THREE.Vector2 | null = null;
        let bestRadius = 0;
        let bestCenterDistance = Number.POSITIVE_INFINITY;
        for (const sign of [1, -1]) {
            const radialDirection = perpendicular.clone().multiplyScalar(sign);
            const denominator = radialDirection.dot(chord);
            // E 位于过 A 的切线方向延长线上时无有限半径圆（ρ → ∞），跳过该候选。
            if (Math.abs(denominator) <= linearTolerance) {
                continue;
            }
            const rho = chordLengthSq / (2 * denominator);
            if (!Number.isFinite(rho) || Math.abs(rho) <= linearTolerance) {
                continue;
            }
            const centerCandidate = anchorLocal.clone().add(radialDirection.multiplyScalar(rho));
            const distanceToPrevious = centerCandidate.distanceTo(previousCenterLocal);
            // 选离旧圆心近的候选，保持圆弧所在侧不翻转。
            if (distanceToPrevious < bestCenterDistance) {
                bestCenterDistance = distanceToPrevious;
                bestCenterLocal = centerCandidate;
                bestRadius = Math.abs(rho);
            }
        }
        if (!bestCenterLocal) {
            return false;
        }

        // 按构造出的圆心重算起终角（保持既有方向与采样段数）。
        const startPointLocal = plane.worldToLocal(this.arcStartPoint.getPosition());
        const endPointLocal = plane.worldToLocal(this.arcEndPoint.getPosition());
        const startAngle = Math.atan2(startPointLocal.y - bestCenterLocal.y, startPointLocal.x - bestCenterLocal.x);
        const rawEndAngle = Math.atan2(endPointLocal.y - bestCenterLocal.y, endPointLocal.x - bestCenterLocal.x);
        // 由原始角度推算几何确定的正扫掠角，再选对应分支的终点角。
        let rawSweep = this.arcDefinition.clockwise ? startAngle - rawEndAngle : rawEndAngle - startAngle;
        while (rawSweep <= 0) {
            rawSweep += Math.PI * 2;
        }
        while (rawSweep > Math.PI * 2) {
            rawSweep -= Math.PI * 2;
        }
        const endAngle = this.resolveEndAngleBySweep(startAngle, rawEndAngle, rawSweep, this.arcDefinition.clockwise);

        this.arcDefinition = {
            ...this.arcDefinition,
            centerWorld: plane.localToWorld(bestCenterLocal),
            radius: bestRadius,
            startAngle,
            endAngle,
        };
        return true;
    }

    /**
     * 解析与当前圆弧共享端点的相切关系来源草图边。
     *
     * 说明：
     * 1. 切线弧创建时会在来源草图边（直线/圆弧/椭圆弧/抛物线/样条）与圆弧之间登记同一个相切关系；
     * 2. 这里从弧上挂载的求解约束中查找该相切关系，并返回共享端点及来源边；
     * 3. 圆角等带半径约束的圆弧同样会命中，但其在 refreshGeometryOnly
     *    中优先走半径锁死分支，不会进入本切线构造。
     * @returns 共享相切来源边与共享点；未命中返回 null。
     */
    private resolveSharedTangentSourceEdge(): {edge: TangentArcSourceEdge; sharedPoint: SketchPointObject} | null {
        for (const constraint of this.constraints.getRelations()) {
            // 只处理相切关系。
            if (constraint.relationType !== SketchRelationKind.Tangent) {
                continue;
            }
            for (const target of constraint.getTargetsSnapshot()) {
                if (!isTangentArcSourceEdge(target)) {
                    continue;
                }
                // 来源边必须与圆弧共享起点或终点，才构成"切线弧 + 来源边"形态。
                const sharedPoint = this.resolveSharedEndpointWithEdge(target);
                if (sharedPoint) {
                    return {edge: target, sharedPoint};
                }
            }
        }
        return null;
    }

    /**
     * 解析来源草图边与当前圆弧共享的端点。
     * @param edge 来源草图边。
     * @returns 共享端点；未共享端点时返回 null。
     */
    private resolveSharedEndpointWithEdge(edge: TangentArcSourceEdge): SketchPointObject | null {
        const edgeStartPoint = getSourceEdgeStartPoint(edge);
        const edgeEndPoint = getSourceEdgeEndPoint(edge);
        if (edgeStartPoint === this.arcStartPoint || edgeEndPoint === this.arcStartPoint) {
            return this.arcStartPoint;
        }
        if (edgeStartPoint === this.arcEndPoint || edgeEndPoint === this.arcEndPoint) {
            return this.arcEndPoint;
        }
        return null;
    }

    /**
     * 根据端点拖拽前后的弦长变化，计算目标扫掠角。
     * @param previousSweep 旧扫掠角。
     * @param previousChordLength 旧弦长。
     * @param nextChordLength 新弦长。
     * @returns 目标扫掠角。
     */
    private resolveSweepForEndpointDrag(
        previousSweep: number,
        previousChordLength: number,
        nextChordLength: number,
    ): number {
        const epsilon = SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength;
        const chordDelta = nextChordLength - previousChordLength;
        if (Math.abs(chordDelta) <= epsilon) {
            return THREE.MathUtils.clamp(
                previousSweep,
                SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
                Math.PI * 2 - SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
            );
        }

        const sweepDelta = Math.abs(chordDelta) / Math.max(
            this.arcDefinition.radius,
            previousChordLength,
            SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragDenominator,
        );
        if (chordDelta > 0) {
            if (previousSweep < SketchArcObject.HALF_CIRCLE_SWEEP) {
                return Math.min(SketchArcObject.HALF_CIRCLE_SWEEP, previousSweep + sweepDelta);
            }
            if (previousSweep > SketchArcObject.HALF_CIRCLE_SWEEP) {
                return Math.max(SketchArcObject.HALF_CIRCLE_SWEEP, previousSweep - sweepDelta);
            }
            return SketchArcObject.HALF_CIRCLE_SWEEP;
        }

        if (previousSweep < SketchArcObject.HALF_CIRCLE_SWEEP) {
            return Math.max(SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep, previousSweep - sweepDelta);
        }
        if (previousSweep > SketchArcObject.HALF_CIRCLE_SWEEP) {
            return Math.min(
                Math.PI * 2 - SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
                previousSweep + sweepDelta,
            );
        }

        return SketchArcObject.HALF_CIRCLE_SWEEP;
    }

    /**
     * 根据两端点和目标扫掠角构建新的圆弧定义。
     * @param startWorld 起点世界坐标。
     * @param endWorld 终点世界坐标。
     * @param targetSweep 目标扫掠角。
     * @returns 新圆弧定义；若无法构建则返回 null。
     */
    private buildArcDefinitionFromEndpointsAndSweep(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        targetSweep: number,
    ): SketchArcDefinition | null {
        const startLocal = this.referencePlane.worldToLocal(startWorld);
        const endLocal = this.referencePlane.worldToLocal(endWorld);
        const chordVector = endLocal.clone().sub(startLocal);
        const chordLength = chordVector.length();
        if (!Number.isFinite(chordLength) || chordLength <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }

        const normalizedTargetSweep = THREE.MathUtils.clamp(
            targetSweep,
            SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
            Math.PI * 2 - SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
        );
        const minorSweep = normalizedTargetSweep <= Math.PI
            ? normalizedTargetSweep
            : Math.PI * 2 - normalizedTargetSweep;
        const safeMinorSweep = THREE.MathUtils.clamp(
            minorSweep,
            SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointDragMinimumSweep,
            Math.PI,
        );
        const radius = chordLength / (2 * Math.sin(safeMinorSweep / 2));
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }

        const midpoint = startLocal.clone().add(endLocal).multiplyScalar(0.5);
        const perpendicular = new THREE.Vector2(-chordVector.y, chordVector.x);
        if (perpendicular.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.arcEndpointPerpendicularLengthSquared) {
            return null;
        }
        perpendicular.normalize();

        const halfChordLength = chordLength * 0.5;
        const centerOffsetLength = Math.sqrt(Math.max(radius * radius - halfChordLength * halfChordLength, 0));
        const previousCenterLocal = this.referencePlane.worldToLocal(this.arcDefinition.centerWorld);
        const candidateCenterA = midpoint.clone().add(perpendicular.clone().multiplyScalar(centerOffsetLength));
        const candidateCenterB = midpoint.clone().add(perpendicular.clone().multiplyScalar(-centerOffsetLength));
        const nextCenterLocal = candidateCenterA.distanceTo(previousCenterLocal) <= candidateCenterB.distanceTo(previousCenterLocal)
            ? candidateCenterA
            : candidateCenterB;

        const startAngle = Math.atan2(startLocal.y - nextCenterLocal.y, startLocal.x - nextCenterLocal.x);
        const rawEndAngle = Math.atan2(endLocal.y - nextCenterLocal.y, endLocal.x - nextCenterLocal.x);
        const endAngle = this.resolveEndAngleBySweep(startAngle, rawEndAngle, normalizedTargetSweep, this.arcDefinition.clockwise);

        return {
            centerWorld: this.referencePlane.localToWorld(nextCenterLocal),
            radius,
            startAngle,
            endAngle,
            clockwise: this.arcDefinition.clockwise,
            segments: this.arcDefinition.segments,
        };
    }

    /**
     * 依据目标扫掠角选择终点角度分支。
     * @param startAngle 起点角度。
     * @param rawEndAngle 终点原始角度。
     * @param targetSweep 目标扫掠角。
     * @param clockwise 当前方向。
     * @returns 对应目标扫掠角的终点角度。
     */
    private resolveEndAngleBySweep(
        startAngle: number,
        rawEndAngle: number,
        targetSweep: number,
        clockwise: boolean,
    ): number {
        const offsets = [-Math.PI * 2, 0, Math.PI * 2];
        let bestEndAngle = rawEndAngle;
        let bestDiff = Number.POSITIVE_INFINITY;

        offsets.forEach((offset) => {
            const candidateEndAngle = rawEndAngle + offset;
            const candidateSweep = clockwise
                ? startAngle - candidateEndAngle
                : candidateEndAngle - startAngle;
            if (candidateSweep <= 0) {
                return;
            }
            const diff = Math.abs(candidateSweep - targetSweep);
            if (diff < bestDiff) {
                bestDiff = diff;
                bestEndAngle = candidateEndAngle;
            }
        });

        return bestEndAngle;
    }

    /**
     * 解析当前圆弧定义对应的正扫掠角。
     * @param definition 圆弧定义。
     * @returns `(0, 2π)` 范围内的扫掠角。
     */
    private resolveSweepFromDefinition(definition: Required<SketchArcDefinition>): number {
        const rawSweep = definition.clockwise
            ? definition.startAngle - definition.endAngle
            : definition.endAngle - definition.startAngle;
        let normalizedSweep = rawSweep;
        while (normalizedSweep <= 0) {
            normalizedSweep += Math.PI * 2;
        }
        while (normalizedSweep > Math.PI * 2) {
            normalizedSweep -= Math.PI * 2;
        }
        return normalizedSweep;
    }

    /**
     * 根据圆弧定义角度解析对应圆周世界坐标。
     * @param angle 目标角度。
     * @returns 对应圆周世界坐标。
     */
    private resolveArcWorldPointByAngle(angle: number): THREE.Vector3 {
        const localCenter = this.referencePlane.worldToLocal(this.arcDefinition.centerWorld);
        return this.referencePlane.localToWorld(new THREE.Vector2(
            localCenter.x + this.arcDefinition.radius * Math.cos(angle),
            localCenter.y + this.arcDefinition.radius * Math.sin(angle),
        ));
    }


    protected setEdgeEndPoint(point: SketchPointObject): void {
        this.replaceEndpointControlPoint('end', point);
    }

    /**
     * 替换指定角色的圆弧端点控制点。
     *
     * @param role 待替换端点角色。
     * @param point 新端点控制点。
     */
    private replaceEndpointControlPoint(role: 'start' | 'end', point: SketchPointObject): void {
        const currentPoint = role === 'start' ? this.arcStartPoint : this.arcEndPoint;
        if (currentPoint === point) {
            return;
        }

        // 先完整解除旧点的拓扑与对象关系，防止历史恢复或轮廓刷新读到幽灵连接边。
        this.detachControlPoint(currentPoint);
        currentPoint.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoint(currentPoint);
        currentPoint.detachEndpointRole();

        if (role === 'start') {
            this.arcStartPoint = point;
        } else {
            this.arcEndPoint = point;
        }

        // 圆弧不拥有辅助对象状态，新点保留自身事件语义并仅登记为新的连接端点。
        point.registerConnectedEdge(this);
        point.attachEndpointRole();
        this.registerRelatedSketchPoint(point);
        this.attachEndpointControlPoint(point);
    }


    /**
     * 基于当前弧线定义重建几何
     */
    protected updateGeometry(): void {
        const localCenter = this.referencePlane.worldToLocal(this.arcDefinition.centerWorld);
        const curve = new THREE.EllipseCurve(
            localCenter.x,
            localCenter.y,
            this.arcDefinition.radius,
            this.arcDefinition.radius,
            this.arcDefinition.startAngle,
            this.arcDefinition.endAngle,
            this.arcDefinition.clockwise,
            0,
        );

        const localPoints = curve.getPoints(this.arcDefinition.segments);
        const worldPoints = localPoints.map((point) => this.referencePlane.localToWorld(new THREE.Vector2(point.x, point.y)));
        const shouldRebuildRaycastAcceleration = this.syncSampledLineGeometry(worldPoints);
        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();
        if (shouldRebuildRaycastAcceleration) {
            ensureRaycastAccelerationForObjectTree(this);
        } else {
            refitRaycastAccelerationForGeometry(this.geometry);
        }

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }

        this.syncCenterPoint();

        // 圆弧几何更新后立即同步默认中点，确保显示与捕捉始终对齐最新几何。
        this.syncMidPoint();


        // 几何刷新后同步象限点位置和可见性。
        this.syncArcQuadrantPoints();

    }

    /**
     * 仅刷新当前圆弧几何，不触发约束求解。
     * 说明：
     * 1. 参数运行时会先静默写入控制点；
     * 2. 圆弧几何依赖 arcDefinition，故刷新前必须先从控制点重建定义；
     * 3. 这样可确保拖动圆心/端点后，模型几何与约束结果保持一致。
     */
    public override refreshGeometryOnly(movedPoint?: SketchPointObject): void {
        // 解析当前被拖拽点是否为该圆弧的起点或终点
        const draggedEndpointRole = this.resolveDraggedEndpointRole(movedPoint);
        if (this.hasRadiusConstraint()) {
            // 半径约束存在时，刷新只允许更新圆心和角度，不能从端点弦长反推覆盖半径。
            this.rebuildRadiusLockedDefinitionFromControlPoints();
        } else if (draggedEndpointRole) {
            // 相切弧端点拖拽优先走"共享切点 + 直线方向"的切线构造，退化时回退扫掠角规则。
            if (!this.rebuildTangentAnchoredArcDefinition(draggedEndpointRole)) {
                // 否则（非相切弧端点、或切线构造退化），按端点拖拽语义重建圆弧定义
                // （保持另一端绝对不动，重算半径与圆心）。
                this.rebuildDefinitionFromDraggedEndpoint(draggedEndpointRole);
            }
        } else {
            // 否则（如拖拽圆心或无拖拽点信息），默认基于所有控制点当前位置计算定义
            this.rebuildDefinitionFromControlPoints();
        }
        // 再按最新定义重建几何。
        this.updateGeometry();
        // 最后吸附端点到精确圆周位置，消除数值误差。
        this.syncEndpointPositions();
        this.lastSyncedCenterWorld = this.arcCenterPoint.getPosition();
    }


    /**
     * 同步圆弧中心点位置。
     */
    private syncCenterPoint(): void {
        this.arcCenterPoint.syncPositionSilently(this.arcDefinition.centerWorld.clone());
        this.lastSyncedCenterWorld = this.arcCenterPoint.getPosition();
    }


    /**
     * 同步默认中点对象位置。
     * 说明：
     * 1. 中点位置统一来自圆弧定义，避免多处重复计算导致漂移；
     * 2. 使用静默同步，防止触发额外约束求解链路。
     */
    private syncMidPoint(): void {
        this.midPoint.syncPositionSilently(this.getMidpointPosition());
    }


    /**
     * 将起终点控制点静默同步到弧定义计算的精确圆周位置。
     * 说明：
     * 1. 根据 arcDefinition 的半径与起终角精确算出圆周坐标；
     * 2. 使用 syncPositionSilently 写入，不触发约束联动；
     * 3. 仅在 syncEndpointsToGeometry 末尾调用（rebuildDefinitionFromControlPoints 之后），
     *    避免在 super 内部 updateGeometry 时干扰角度重建所需的控制点读取。
     */
    private syncEndpointPositions(): void {
        const { centerWorld, radius, startAngle, endAngle } = this.arcDefinition;
        const localCenter = this.referencePlane.worldToLocal(centerWorld);

        // 根据弧定义精确计算起终点在圆周上的世界坐标。
        const newStartWorld = this.referencePlane.localToWorld(new THREE.Vector2(
            localCenter.x + radius * Math.cos(startAngle),
            localCenter.y + radius * Math.sin(startAngle),
        ));
        const newEndWorld = this.referencePlane.localToWorld(new THREE.Vector2(
            localCenter.x + radius * Math.cos(endAngle),
            localCenter.y + radius * Math.sin(endAngle),
        ));

        // 静默写入，不触发约束联动。
        this.arcStartPoint.syncPositionSilently(newStartWorld);
        this.arcEndPoint.syncPositionSilently(newEndWorld);
    }


    /**
     * 获取圆弧象限点只读集合。
     * @returns 象限点集合（包含 4 个标准角度对应的 SketchPointObject）。
     */
    public getQuadrantPoints(): ReadonlySet<SketchPointObject> {
        return this.arcQuadrantPoints;
    }

    /**
     * 构造时创建四个标准象限点对象。
     * 说明：
     * 1. 为每个标准角（0°/90°/180°/270°）创建一个 SketchPointObject；
     * 2. 设置 isQuadrantPoint = true，使 SketchGroup 自动将其纳入 _quadrantPointSet；
     * 3. 默认全部不可见，由 syncArcQuadrantPoints 根据弧范围决定可见性；
     * 4. 注册为关联草图点，确保 SketchGroup 感知到这些点。
     */
    private createArcQuadrantPoints(): void {
        const material = PointsMaterialFactory.getSketchControlPointSpriteMaterial();

        for (const angle of SketchArcObject.QUADRANT_ANGLES) {
            // 计算该象限角对应的世界坐标。
            const worldPos = this.computeQuadrantWorldPos(angle);
            const point = new SketchPointObject({
                position: worldPos,
                pointKind: SketchPointKind.ControlPoint,
                material,
                referencePlane: this.referencePlane,
            });
            // 标记为象限点，SketchGroup 遍历时会将其加入 _quadrantPointSet。
            point.isQuadrantPoint = true;
            // 始终不渲染（和圆的象限点行为一致，捕捉由策略临时显示）。
            point.visible = false;
            // 初始不参与捕捉，syncArcQuadrantPoints 会按弧范围更新。
            point.isActiveForSnap = false;

            if (this.isDrawingPreviewArc) {
                // 预览圆弧自动生成的象限点也不应参与点捕捉。
                point.isDrawingPreviewPoint = true;
            }

            this.arcQuadrantPoints.add(point);
            this.angleToQuadrantPoint.set(angle, point);
            this.add(point);
        }

        // 注册为关联草图点，保证 SketchGroup 可跟踪。
        this.registerRelatedSketchPoints(Array.from(this.arcQuadrantPoints));

        // 初始同步位置和可捕捉状态。
        this.syncArcQuadrantPoints();
    }

    /**
     * 同步象限点位置和可捕捉状态。
     * 说明：
     * 1. 遍历四个标准角；
     * 2. 更新对应象限点的世界坐标；
     * 3. 若角度在当前弧范围内则 isActiveForSnap=true，否则 false；
     * 4. visible 始终保持 false（不渲染），由捕捉策略临时切换显示。
     */
    private syncArcQuadrantPoints(): void {
        const { startAngle, endAngle, clockwise } = this.arcDefinition;

        this.angleToQuadrantPoint.forEach((point, angle) => {
            // 更新位置。
            const worldPos = this.computeQuadrantWorldPos(angle);
            point.syncPositionSilently(worldPos);
            // 根据当前弧范围决定是否参与捕捉（不影响渲染可见性）。
            point.isActiveForSnap = this.isAngleInArcRange(angle, startAngle, endAngle, clockwise);
        });
    }

    /**
     * 计算指定角度在参考平面上对应的世界坐标（位于圆弧圆周上）。
     * @param angle 角度（弧度）。
     * @returns 世界坐标。
     */
    private computeQuadrantWorldPos(angle: number): THREE.Vector3 {
        const { centerWorld, radius } = this.arcDefinition;
        const localCenter = this.referencePlane.worldToLocal(centerWorld);
        return this.referencePlane.localToWorld(
            localCenter.x + radius * Math.cos(angle),
            localCenter.y + radius * Math.sin(angle),
        );
    }

    /**
     * 判断给定角度是否落在圆弧扫掠范围内。
     * 说明：将所有角度规范化到 [0, 2π)，再按旋转方向判断。
     * @param angle 待判断角度（弧度）。
     * @param startAngle 弧起始角（弧度）。
     * @param endAngle 弧终止角（弧度）。
     * @param clockwise 是否为顺时针方向。
     * @returns 角度在弧范围内返回 true。
     */
    private isAngleInArcRange(
        angle: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
    ): boolean {
        const TWO_PI = Math.PI * 2;
        // 将角度规范化到 [0, 2π)。
        const normalize = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
        const a = normalize(angle);
        const s = normalize(startAngle);
        const e = normalize(endAngle);

        if (clockwise) {
            // 顺时针：从 s 递减到 e。
            if (s >= e) {
                return a <= s && a >= e;
            }
            // 跨越 0 点。
            return a <= s || a >= e;
        } else {
            // 逆时针：从 s 递增到 e。
            if (s <= e) {
                return a >= s && a <= e;
            }
            // 跨越 0 点。
            return a >= s || a <= e;
        }
    }


    /**
     * 绑定所属封闭线段几何对象。
     * @param geometry 目标封闭线段几何对象或槽口对象。
     * @throws 当当前圆弧已绑定其他封闭几何对象时抛出错误。
     */
    public attachClosedCollection(geometry: SketchCollectionObject | SketchSlotObject): void {
        if (this.closedCollection && this.closedCollection !== geometry) {
            throw new Error('当前圆弧已关联其他封闭线段几何对象。');
        }
        this.closedCollection = geometry;
        if (isSketchSlotObject(geometry)) {
            this.contour = geometry.contour;
            return;
        }
        this.contour = geometry.getContour();
    }

    /**
     * 解绑所属封闭线段几何对象。
     * @param geometry 若传入，仅在匹配时才解绑。
     */
    public detachClosedCollection(geometry?: SketchCollectionObject | SketchSlotObject): void {
        if (geometry && this.closedCollection !== geometry) {
            return;
        }
        this.closedCollection = null;
        this.clearContour();
    }

    /**
     * 圆弧对象是否可创建轮廓。
     * 说明：
     * 1. 单条圆弧本身不构成闭合轮廓；
     * 2. 仅当圆弧已被封闭线段几何对象接管时，才可由该几何对象生成轮廓。
     * @returns 若已关联封闭线段几何对象则返回 true。
     */
    public canCreateContour(): boolean {
        return this.closedCollection !== null;
    }

    /**
     * 创建圆弧所属轮廓。
     * 说明：
     * 1. 实际轮廓由封闭线段几何对象统一生成；
     * 2. 当前方法仅做能力校验与委托。
     * @returns 创建后的轮廓对象。
     */
    public createContour(): SketchContour {
        return this.createContourFromHost(this.closedCollection, '圆弧');
    }

}

